import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDataCache } from '../contexts/DataCacheContext';
import configService from '../services/configService';
import { useToast } from './Toast';

function getFlagEmoji(countryCode) {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

function BehaviorSettings({ onComplete, adminBusinessId }) {
  const { user } = useAuth();
  const { updateCache } = useDataCache();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    rememberCallers: false,
    bossPhone: '',
    bossName: '',
    bossEmail: '',
    escalationTexts: false
  });
  const [countryCode, setCountryCode] = useState('+1');
  
  const effectiveUserId = adminBusinessId || user?.attributes?.sub;
  const settingsRef = useRef(settings);
    
  // Keep ref in sync with state
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  const saveBehaviorSettings = useCallback(async () => {
    if (!effectiveUserId) return false;
    
    setLoading(true);
    try {
      // Get current settings from ref
      const currentSettings = settingsRef.current;
      
      // Format phone number for storage (with country code prefix)
      const phoneDigits = currentSettings.bossPhone.replace(/\D/g, '');
      const formattedPhone = phoneDigits ? `${countryCode}${phoneDigits}` : '';
      
      const behaviorData = {
        remember_past_callers: currentSettings.rememberCallers,
        manager_number: formattedPhone,
        manager_name: currentSettings.bossName.trim(),
        escalation_email: currentSettings.bossEmail.trim(),
        text_escalation_to_manager: currentSettings.escalationTexts
      };

      if (adminBusinessId) {
        await configService.saveAdminBehaviorSettings(effectiveUserId, behaviorData);
      } else {
        await configService.saveBehaviorSettings(behaviorData);
      }
      
      const config = adminBusinessId
        ? await configService.getAdminUserConfig(effectiveUserId)
        : await configService.getBusinessConfigByUserId(effectiveUserId);
      updateCache('settings', { businessConfig: config });
      return true;
    } catch (error) {
      console.error('❌ Error saving Administrator Contact configuration:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [effectiveUserId, countryCode]);

  // Expose save function globally for Setup.js
  useEffect(() => {
    window.behaviorSettingsSave = saveBehaviorSettings;
    return () => {
      delete window.behaviorSettingsSave;
    };
  }, [saveBehaviorSettings]);

  useEffect(() => {
    loadBehaviorSettings();
  }, [user]);

  const loadBehaviorSettings = async () => {
    if (!effectiveUserId) return;
    
    try {
      const config = adminBusinessId
        ? await configService.getAdminUserConfig(effectiveUserId)
        : await configService.getBusinessConfigByUserId(effectiveUserId);
      
      console.log('[BehaviorSettings] Loaded config:', {
        manager_name: config?.manager_name,
        isAdminMode: !!adminBusinessId
      });
      
      if (config) {
        // Extract country code and format phone number for display
        let displayPhone = '';
        let extractedCountryCode = '+1';
        
        if (config.manager_number) {
          // Extract all digits from the stored number
          const allDigits = config.manager_number.replace(/\D/g, '');
          
          // Country code mapping based on dropdown options
          const countryCodeMap = {
            '1': { code: '+1', length: 1 },    // US/CA
            '44': { code: '+44', length: 2 },  // GB
            '33': { code: '+33', length: 2 },  // FR
            '49': { code: '+49', length: 2 },  // DE
            '39': { code: '+39', length: 2 },  // IT
            '34': { code: '+34', length: 2 },  // ES
            '61': { code: '+61', length: 2 },  // AU
            '81': { code: '+81', length: 2 }   // JP
          };
          
          // Find matching country code
          let foundMatch = false;
          for (const [digits, info] of Object.entries(countryCodeMap)) {
            if (allDigits.startsWith(digits)) {
              extractedCountryCode = info.code;
              const phoneDigits = allDigits.slice(info.length);
              
              // Format based on country
              if (info.code === '+1' && phoneDigits.length === 10) {
                displayPhone = `${phoneDigits.slice(0, 3)}-${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`;
              } else {
                displayPhone = phoneDigits;
              }
              foundMatch = true;
              break;
            }
          }
          
          // Fallback for unrecognized format
          if (!foundMatch) {
            if (allDigits.length === 10) {
              // Assume US/Canada without country code
              extractedCountryCode = '+1';
              displayPhone = `${allDigits.slice(0, 3)}-${allDigits.slice(3, 6)}-${allDigits.slice(6)}`;
            } else {
              extractedCountryCode = '+1';
              displayPhone = allDigits;
            }
          }
        }
        
        setCountryCode(extractedCountryCode);
        setSettings({
          rememberCallers: config.remember_past_callers || false,
          bossPhone: displayPhone,
          bossName: config.manager_name || '',
          bossEmail: config.escalation_email || '',
          escalationTexts: config.text_escalation_to_manager || false
        });
      }
    } catch (error) {
      console.error('Error loading behavior settings:', error);
    }
  };

  // Auto-save function for input fields
  const handleAutoSave = useCallback(async () => {
    if (!effectiveUserId) return;
    
    setSaving(true);
    try {
      const phoneDigits = settings.bossPhone.replace(/\D/g, '');
      const formattedPhone = phoneDigits ? `${countryCode}${phoneDigits}` : '';
      
      const behaviorData = {
        remember_past_callers: settings.rememberCallers,
        manager_number: formattedPhone,
        manager_name: settings.bossName.trim(),
        escalation_email: settings.bossEmail.trim(),
        text_escalation_to_manager: settings.escalationTexts
      };

      if (adminBusinessId) {
        await configService.saveAdminBehaviorSettings(effectiveUserId, behaviorData);
      } else {
        await configService.saveBehaviorSettings(behaviorData);
      }
      
      const config = adminBusinessId
        ? await configService.getAdminUserConfig(effectiveUserId)
        : await configService.getBusinessConfigByUserId(effectiveUserId);
      updateCache('settings', { businessConfig: config });
      showToast('Saved successfully!');
      if (onComplete) onComplete();
    } catch (error) {
      console.error('❌ Error auto-saving settings:', error);
      showToast('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  }, [effectiveUserId, countryCode, settings, onComplete, showToast, updateCache]);





  return (
    <div style={{ width: '100%', maxWidth: '500px', margin: '0 auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        
        {/* Boss Information */}
        <div style={{ padding: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500' }}>
                Name
              </label>
              <input
                type="text"
                value={settings.bossName}
                onChange={(e) => setSettings(prev => ({ ...prev, bossName: e.target.value }))}
                onBlur={handleAutoSave}
                placeholder="e.g., Mr. Smith, Sarah, Boss"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
              />
            </div>            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500' }}>
                Phone Number
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
                <select 
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  onBlur={handleAutoSave}
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    flexShrink: 0
                  }}
                >
                  <option value="+1">{getFlagEmoji('US')} +1</option>
                  <option value="+1">{getFlagEmoji('CA')} +1</option>
                  <option value="+44">{getFlagEmoji('GB')} +44</option>
                  <option value="+33">{getFlagEmoji('FR')} +33</option>
                  <option value="+49">{getFlagEmoji('DE')} +49</option>
                  <option value="+39">{getFlagEmoji('IT')} +39</option>
                  <option value="+34">{getFlagEmoji('ES')} +34</option>
                  <option value="+61">{getFlagEmoji('AU')} +61</option>
                  <option value="+81">{getFlagEmoji('JP')} +81</option>
                </select>
                <input
                  type="tel"
                  value={settings.bossPhone}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Remove any non-digit characters
                    const digits = value.replace(/\D/g, '');
                    
                    // Format as phone number and limit to 10 digits
                    let formatted = digits.slice(0, 10);
                    if (formatted.length >= 6) {
                      formatted = `${formatted.slice(0, 3)}-${formatted.slice(3, 6)}-${formatted.slice(6)}`;
                    } else if (formatted.length >= 3) {
                      formatted = `${formatted.slice(0, 3)}-${formatted.slice(3)}`;
                    }
                    
                    setSettings(prev => ({ ...prev, bossPhone: formatted }));
                  }}
                  onBlur={handleAutoSave}
                  placeholder="123-456-7890"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>

          </div>
        </div>

        {/* Email Transcripts */}
        <div style={{ padding: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500' }}>
              Send Call Transcripts to Email
            </label>
            <input
              type="email"
              value={settings.bossEmail}
              onChange={(e) => setSettings(prev => ({ ...prev, bossEmail: e.target.value }))}
              onBlur={handleAutoSave}
              placeholder="call_logs@company.com"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem'
              }}
            />
          </div>
        </div>

      </div>

      {(loading || saving) && (
        <div style={{ textAlign: 'center', marginTop: '1rem', color: '#64748b' }}>
          {saving ? 'Saving settings...' : 'Loading settings...'}
        </div>
      )}
    </div>
  );
}

export default BehaviorSettings;